import React, { useState, useRef, useEffect } from 'react';
import { getCsrfToken } from '../utils/auth';
import DocPlacement from './DocPlacement';
import '../styles/AddItemModal.css';

/**
 * Inline "add item + trace" modal, with an AI (Ask Paul) generate mode.
 *
 * Manual mode: creates one item at ctx.levelNum and links it to ctx.parentKey.
 * AI mode: asks Ask Paul to generate child items from the parent item, shows them as
 *   editable draft cards, and lets the user Add each one (create + trace, plus test steps).
 *
 * Type and version are inherited from the level's settings.
 *
 * ctx: { levelNum, parentKey, childType, childKeyPrefix, l1Id, parentLevelNum, childTypeCode }
 * onDone(result):  manual single add — closes the modal. result = { status, item, error }
 * onAdded(result): AI per-card add — reconciles the grid but keeps the modal open
 *
 * api (optional): who the modal talks to. Omitted = the matrix panel's own routes. Risk passes
 *   its own (＋ Add mitigation on a risk) so both modules share one add / ✨ AI screen.
 *   { key, checkAi() → {available, detail} | null, generate() → {ok, data},
 *     add({name, description, steps}) → {ok, data}, dupCheck?(drafts) → {ok, data} }
 * headerExtra: a node under the title (Risk's mitigation type picker).
 *
 * "Place in document" (DHF, v4.34.0): when `io.placement` is set (the matrix's own routes), the
 * documents holding this level's items are offered, and every add — manual or ✨ AI — creates
 * the item under the chosen paragraph instead of in the pool. Hidden when the level has none.
 */
export function AddItemModal({ setupId, ctx, canAskPaul = true, onClose, onDone, onAdded, api, headerExtra }) {
  const io = api || matrixApi(setupId, ctx);
  const [mode, setMode] = useState('manual');    // 'manual' | 'ai'
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Risk's validity check refused the type — {reason, better_types:[{code,label}]}.
  const [unfitTypes, setUnfitTypes] = useState([]);
  const [placeAt, setPlaceAt] = useState(null);   // {docId, parentId, parentLabel} | null = pool
  const nameRef = useRef(null);
  const descRef = useRef(null);
  const isRoot = !ctx.parentKey;   // new L1 root item — no parent to trace to

  // Ask Paul needs a prompt per (source type → target type) pair. Check that this pairing has
  // one BEFORE offering the button, so the AI flow is never a dead end.
  const [aiCheck, setAiCheck] = useState({ status: 'idle', detail: '' });
  const canOfferAi = canAskPaul && !isRoot;

  useEffect(() => {
    if (!canOfferAi) return;
    let cancelled = false;
    setAiCheck({ status: 'checking', detail: '' });
    io.checkAi()
      .then((data) => {
        if (cancelled) return;
        // Fail OPEN: only a definitive "no prompt for this pair" hides the button. If the
        // check itself failed, still offer AI — /ai/generate re-checks server-side.
        if (!data) { setAiCheck({ status: 'available', detail: '' }); return; }
        setAiCheck({
          status: data.available ? 'available' : 'none',
          detail: data.detail || '',
        });
      })
      .catch(() => { if (!cancelled) setAiCheck({ status: 'available', detail: '' }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOfferAi, io.key]);

  useEffect(() => {
    if (mode !== 'manual') return;
    const t = setTimeout(() => nameRef.current && nameRef.current.focus(), 40);
    return () => clearTimeout(t);
  }, [mode]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  const exec = (cmd) => {
    if (cmd === 'createLink') {
      const url = window.prompt('Link URL', 'https://');
      if (url) document.execCommand(cmd, false, url);
    } else {
      document.execCommand(cmd, false, null);
    }
    descRef.current && descRef.current.focus();
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      nameRef.current && nameRef.current.focus();
      return;
    }
    setSubmitting(true);
    setError('');
    setUnfitTypes([]);
    try {
      const description = descRef.current ? descRef.current.innerHTML : '';
      const { ok, data } = await io.add({ name: trimmed, description, steps: null, placeAt });
      if (!ok || !data.IsSuccess) {
        setError((data && data.detail) || 'Could not create the item.');
        setUnfitTypes((data && data.unfit && data.unfit.better_types) || []);
        setSubmitting(false);
        return;
      }
      onDone(data);
    } catch (err) {
      setError('Error: ' + err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="ai-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className={`ai-modal${mode === 'ai' ? ' ai-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="ai-title">
        <div className={`ai-head${headerExtra ? ' ai-head--extra' : ''}`}>
          <div className="ai-head-main">
            <h3 id="ai-title">Add {ctx.childType}</h3>
            <div className="ai-sub">{ctx.subtitle || "Created in this level's project & version"}</div>
            {isRoot
              ? <span className="ai-traced">＋ new top-level (L1) item</span>
              : <span className="ai-traced">🔗 traced to <b>{ctx.parentKey}</b></span>}
          </div>
          <div className="ai-head-right">
            {mode === 'manual' && canOfferAi && aiCheck.status === 'available' && (
              <button
                className="ai-magic"
                onClick={() => setMode('ai')}
                disabled={submitting}
                title={`Generate ${ctx.childType} with AI (Ask Paul)`}
              >
                ✨ Generate with AI
              </button>
            )}
            {mode === 'manual' && canOfferAi
              && (aiCheck.status === 'checking' || aiCheck.status === 'idle') && (
              <span className="ai-magic-checking" title="Checking Ask Paul availability…">
                <span className="ai-magic-spinner" aria-hidden="true" />
                Checking Ask Paul…
              </span>
            )}
            {mode === 'manual' && canOfferAi && aiCheck.status === 'none' && (
              <span
                className="ai-magic-off"
                title={aiCheck.detail || `No Ask Paul prompt is configured for ${ctx.childType}.`}
              >
                ✨ AI unavailable for this level
              </span>
            )}
            <button className="ai-x" onClick={onClose} disabled={submitting} aria-label="Close">×</button>
          </div>
          {headerExtra && <div className="ai-head-extra">{headerExtra}</div>}
        </div>

        {io.placement && (
          <div className="ai-place-wrap">
            <DocPlacement setupId={setupId} levelNum={ctx.levelNum} blockId={ctx.childBlockId || ''}
                          onChange={setPlaceAt} disabled={submitting} />
          </div>
        )}

        {mode === 'manual' ? (
          <>
            <div className="ai-body">
              <div className="ai-field">
                <label htmlFor="ai-name">Name</label>
                <input
                  id="ai-name"
                  ref={nameRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder={isRoot ? `e.g. a new ${ctx.childType}` : `e.g. ${ctx.childType} for ${ctx.parentKey}`}
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>

              <div className="ai-field">
                <label>Description</label>
                <div className="ai-rtbar">
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} title="Bold"><b>B</b></button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} title="Italic"><i>I</i></button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('createLink')} title="Insert link">🔗</button>
                  <span className="ai-fn">basic HTML</span>
                </div>
                <div
                  ref={descRef}
                  className="ai-editor"
                  contentEditable={!submitting}
                  suppressContentEditableWarning
                  data-ph="Describe the item… bold, italic and links are supported"
                />
              </div>

              {error && <div className="ai-error">{error}</div>}
              {unfitTypes.length > 0 && io.pickType && (
                <div className="ai-unfit-types ai-unfit-types--manual">
                  {unfitTypes.map((t) => (
                    <button key={t.code} type="button" className="btn-secondary"
                      onClick={() => { io.pickType(t.code); setError(''); setUnfitTypes([]); }}>
                      Use {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="ai-foot">
              <button className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Creating…' : `Create ${ctx.childType}`}
              </button>
            </div>
          </>
        ) : (
          <AiGenerate
            io={io}
            ctx={ctx}
            placeAt={placeAt}
            onBack={() => setMode('manual')}
            onClose={onClose}
            onAdded={onAdded}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────  AI (Ask Paul) mode  ───────────────────────────── */

/**
 * Editable HTML viewer — renders `value` as rendered HTML (not raw <tags>) while
 * staying editable. Ask Paul returns descriptions as HTML, so a plain <textarea>
 * would show the raw markup; this shows it formatted and keeps it editable.
 * The DOM is (re)synced only when `value` changes externally (e.g. Regenerate),
 * so typing never clobbers the caret.
 */
function HtmlEditable({ value, onChange, disabled, placeholder, className }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== (value || '')) el.innerHTML = value || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div
      ref={ref}
      className={className}
      contentEditable={!disabled}
      suppressContentEditableWarning
      data-ph={placeholder}
      onInput={(e) => onChange(e.currentTarget.innerHTML)}
    />
  );
}

function AiGenerate({ io, ctx, placeAt, onBack, onClose, onAdded }) {
  const [status, setStatus] = useState('loading');   // 'loading' | 'ready' | 'error'
  const [error, setError] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [drafts, setDrafts] = useState([]);
  // The AI judged the chosen type unable to address the parent (Risk: a mechanical
  // requirement for a privacy risk) — {reason, better_types:[{code,label}]}.
  const [unfit, setUnfit] = useState(null);
  // Opt-in duplicate screen. 'idle' | 'checking' | 'done'
  const [dupCheck, setDupCheck] = useState({ status: 'idle', aiUsed: false, error: '' });

  const generate = async () => {
    setStatus('loading');
    setError('');
    try {
      const { ok, data } = await io.generate();
      if (!ok || !data.IsSuccess) {
        // No Ask Paul prompt configured for this source→child pairing — not retryable,
        // guide the user to manual entry rather than offering a "Try again" button.
        if (data && data.reason === 'no_prompt') {
          setError((data && data.detail) || `No AI prompt is configured to generate ${ctx.childType}.`);
          setStatus('noprompt');
          return;
        }
        setError((data && data.detail) || 'AI generation failed.');
        setStatus('error');
        return;
      }
      if (data.unfit) {
        setUnfit(data.unfit);
        setDrafts([]);
        setStatus('unfit');
        return;
      }
      setUnfit(null);
      setIsTest(!!data.is_test);
      setPrompt(data.prompt || null);
      setDrafts((data.drafts || []).map((d, i) => ({
        key: `${Date.now()}-${i}`,
        name: d.name || '',
        description: d.description || '',
        steps: Array.isArray(d.steps) ? d.steps.map((s) => ({
          description: s.description || '', expected: s.expected || '',
        })) : null,
        _status: 'new',      // 'new' | 'adding' | 'added' | 'error'
        _error: '',
      })));
      setStatus('ready');
    } catch (err) {
      setError('Error: ' + err.message);
      setStatus('error');
    }
  };

  // Re-runs when the target changes (Risk's type picker) — drafts are written for one type.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { generate(); }, [io.key]);

  const patch = (key, fields) =>
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...fields } : d)));

  const patchStep = (key, idx, field, value) =>
    setDrafts((ds) => ds.map((d) => {
      if (d.key !== key) return d;
      const steps = (d.steps || []).map((s, i) => (i === idx ? { ...s, [field]: value } : s));
      return { ...d, steps };
    }));

  const addStepRow = (key) =>
    setDrafts((ds) => ds.map((d) =>
      d.key === key ? { ...d, steps: [...(d.steps || []), { description: '', expected: '' }] } : d));

  const removeStepRow = (key, idx) =>
    setDrafts((ds) => ds.map((d) =>
      d.key === key ? { ...d, steps: (d.steps || []).filter((_, i) => i !== idx) } : d));

  const removeDraft = (key) => setDrafts((ds) => ds.filter((d) => d.key !== key));

  // Opt-in: screen every still-addable draft for duplicates before adding. Read-only —
  // searches this level's filter (keyword-scoped) + the parent's relations, hybrid-matches,
  // and flags suspects. Sends the FULL draft list so the backend's draft-vs-draft indices line up.
  const runDupCheck = async () => {
    setDupCheck({ status: 'checking', aiUsed: false, error: '' });
    try {
      const { ok, data } = await io.dupCheck(
        drafts.map((d) => ({ name: d.name || '', description: d.description || '' })));
      if (!ok || !data.IsSuccess) {
        setDupCheck({ status: 'idle', aiUsed: false, error: (data && data.detail) || 'Duplicate check failed.' });
        return;
      }
      const byIndex = {};
      (data.results || []).forEach((r) => { byIndex[r.index] = r; });
      setDrafts((ds) => ds.map((d, i) => (byIndex[i] ? { ...d, _dup: byIndex[i], _dupAck: false } : d)));
      setDupCheck({ status: 'done', aiUsed: !!data.ai_used, error: '' });
    } catch (err) {
      setDupCheck({ status: 'idle', aiUsed: false, error: 'Error: ' + err.message });
    }
  };

  const addDraft = async (draft) => {
    if (!draft.name.trim()) {
      patch(draft.key, { _error: 'Name is required.' });
      return;
    }
    patch(draft.key, { _status: 'adding', _error: '' });
    try {
      const steps = isTest && draft.steps
        ? draft.steps.filter((s) => (s.description || '').trim() || (s.expected || '').trim())
        : null;
      const hasSteps = !!(steps && steps.length);
      // Description is the intro/objective only (steps live in the table and are
      // added separately via QW_Add_Step), so no duplication.
      const { ok, data } = await io.add({
        name: draft.name.trim(), description: draft.description || '', steps: hasSteps ? steps : null, placeAt,
      });
      if (!ok || !data.IsSuccess) {
        patch(draft.key, { _status: 'error', _error: (data && data.detail) || 'Could not add.' });
        return;
      }
      patch(draft.key, {
        _status: 'added', _added: data.item || null,
        // Created but not (properly) linked — still shown as added, so it is not lost.
        _error: data.status === 'trace_failed' ? (data.error || 'Created, but the link failed.') : (data.warning || ''),
      });
      if (onAdded) onAdded(data);
    } catch (err) {
      patch(draft.key, { _status: 'error', _error: 'Error: ' + err.message });
    }
  };

  const addedCount = drafts.filter((d) => d._status === 'added').length;
  const suspectCount = drafts.filter(
    (d) => d._dup?.status === 'suspect' && !d._dupAck && d._status !== 'added'
  ).length;

  return (
    <>
      <div className="ai-gen-bar">
        <div className="ai-gen-info">
          {status === 'loading' && <span className="ai-gen-spin">✨ Asking Paul to generate {ctx.childType}…</span>}
          {status === 'ready' && (
            <span>
              ✨ {drafts.length} suggested {ctx.childType}
              {prompt?.Name ? <span className="ai-gen-prompt"> · via “{prompt.Name}”</span> : null}
              {addedCount > 0 ? <span className="ai-gen-added"> · {addedCount} added</span> : null}
              {dupCheck.status === 'done' ? (
                suspectCount > 0
                  ? <span className="ai-gen-dup"> · 🔴 {suspectCount} possible duplicate{suspectCount > 1 ? 's' : ''}</span>
                  : <span className="ai-gen-added"> · ✓ no duplicates</span>
              ) : null}
              {dupCheck.error ? <span className="ai-gen-err"> · {dupCheck.error}</span> : null}
            </span>
          )}
          {status === 'error' && <span className="ai-gen-err">⚠ {error}</span>}
          {status === 'noprompt' && <span className="ai-gen-err">✨ AI generation isn’t available here</span>}
          {status === 'unfit' && <span className="ai-gen-err">⚠ A {ctx.childType} can’t control {ctx.parentKey}</span>}
        </div>
        <div className="ai-gen-actions">
          <button className="ai-link" onClick={onBack} disabled={status === 'loading'}>← Manual</button>
          {status === 'ready' && io.dupCheck && drafts.some((d) => d._status !== 'added') && (
            <button
              className={`ai-dupcheck${dupCheck.status === 'checking' ? ' is-checking' : ''}${dupCheck.status === 'done' ? ' is-done' : ''}`}
              onClick={runDupCheck}
              disabled={dupCheck.status === 'checking'}
              title={ctx.dupHint || "Check these drafts against existing items in this level's filter and the parent's relations"}
            >
              <span className="ai-dupcheck-ic" aria-hidden="true">
                {dupCheck.status === 'checking' ? '🔍' : dupCheck.status === 'done' ? '↻' : '🔍'}
              </span>
              <span className="ai-dupcheck-label">
                {dupCheck.status === 'checking'
                  ? 'Checking…'
                  : dupCheck.status === 'done' ? 'Re-check' : 'Check duplicates'}
              </span>
              {dupCheck.status === 'checking' && <span className="ai-dupcheck-bar" aria-hidden="true" />}
            </button>
          )}
          {status !== 'noprompt' && (
            <button className="ai-regen" onClick={generate} disabled={status === 'loading'}>
              <span className="ai-regen-ic" aria-hidden="true">↻</span>
              <span>{status === 'loading' ? 'Generating…' : 'Regenerate'}</span>
            </button>
          )}
        </div>
      </div>

      <div className="ai-body ai-gen-body">
        {status === 'loading' && (
          <div className="ai-gen-loading">
            <div className="ai-gen-loader" />
            <p>This can take a few seconds — the AI is reading <b>{ctx.parentKey}</b> and drafting {ctx.childType}.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="ai-gen-empty">
            <p>{error}</p>
            <button className="btn-primary" onClick={generate}>Try again</button>
          </div>
        )}

        {status === 'unfit' && unfit && (
          <div className="ai-gen-empty ai-gen-unfit">
            <p><b>This type can’t mitigate {ctx.parentKey}.</b> {unfit.reason}</p>
            {(unfit.better_types || []).length > 0 && io.pickType ? (
              <>
                <p>Types that could:</p>
                <div className="ai-unfit-types">
                  {unfit.better_types.map((t) => (
                    <button key={t.code} className="btn-primary" onClick={() => io.pickType(t.code)}>
                      ✨ {t.label}
                    </button>
                  ))}
                </div>
              </>
            ) : <p>Pick another type above.</p>}
          </div>
        )}

        {status === 'noprompt' && (
          <div className="ai-gen-empty ai-gen-noprompt">
            <div className="ai-noprompt-icon">✨</div>
            <p>{error}</p>
            <button className="btn-primary" onClick={onBack}>← Add {ctx.childType} manually</button>
          </div>
        )}

        {status === 'ready' && drafts.length === 0 && (
          <div className="ai-gen-empty"><p>No suggestions were returned. Try Regenerate.</p></div>
        )}

        {status === 'ready' && drafts.map((d, di) => {
          const flagged = d._dup?.status === 'suspect' && !d._dupAck && d._status !== 'added';
          return (
          <div key={d.key} className={`ai-card ai-card-${d._status}${flagged ? ' ai-card-suspect' : ''}`}>
            <div className="ai-card-head">
              <span className="ai-card-n">{di + 1}</span>
              <input
                className="ai-card-name"
                type="text"
                // Editing the name invalidates a prior verdict — clear it so a stale ✓/🔴 is never shown.
                onChange={(e) => patch(d.key, { name: e.target.value, _dup: null, _dupAck: false })}
                value={d.name}
                placeholder={`${ctx.childType} name`}
                disabled={d._status === 'added' || d._status === 'adding'}
              />
              {d._status === 'added' ? (
                <span className="ai-card-done">
                  ✓ Added
                  {(d._added?.display_key || d._added?.key) && (
                    d._added?.url ? (
                      <a
                        className="ai-card-key"
                        href={d._added.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in Orcanos"
                      >
                        {d._added.display_key || d._added.key}
                      </a>
                    ) : (
                      <span className="ai-card-key">{d._added.display_key || d._added.key}</span>
                    )
                  )}
                </span>
              ) : flagged ? (
                <div className="ai-dup-actions">
                  <button
                    className="ai-dup-anyway"
                    onClick={() => { patch(d.key, { _dupAck: true }); addDraft(d); }}
                    disabled={d._status === 'adding'}
                    title="Add this item despite the possible duplicate"
                  >
                    Add anyway
                  </button>
                  <button className="ai-dup-remove" onClick={() => removeDraft(d.key)} title="Discard this draft">
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  className="btn-primary ai-card-add"
                  onClick={() => addDraft(d)}
                  disabled={d._status === 'adding'}
                >
                  {d._status === 'adding' ? 'Adding…' : 'Add'}
                  {d._dup?.status === 'clear' ? <span className="ai-card-clear" title="No duplicates found"> ✓</span> : null}
                </button>
              )}
            </div>

            {flagged && (
              <div className="ai-dup-warn">
                <div className="ai-dup-warn-h">
                  🔴 Possible duplicate{d._dup.matches.length > 1 ? 's' : ''} — review before adding
                </div>
                <ul>
                  {d._dup.matches.map((m, mi) => (
                    <li key={mi}>
                      {m.source === 'draft' ? (
                        <span className="ai-dup-key">Draft #{(m.draft_index ?? 0) + 1} in this batch</span>
                      ) : m.url ? (
                        <a className="ai-dup-key" href={m.url} target="_blank" rel="noopener noreferrer" title="Open in Orcanos">
                          {m.key || m.name}
                        </a>
                      ) : (
                        <span className="ai-dup-key">{m.key || m.name}</span>
                      )}
                      {m.source !== 'draft' && m.name && m.key ? <span className="ai-dup-nm"> — {m.name}</span> : null}
                      {m.reason ? <span className="ai-dup-rs"> · {m.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isTest && d.steps ? (
              <>
                <HtmlEditable
                  className="ai-card-desc ai-test-desc ai-card-desc-html"
                  value={d.description}
                  onChange={(html) => patch(d.key, { description: html })}
                  placeholder="Test description / objective"
                  disabled={d._status === 'added' || d._status === 'adding'}
                />
                <div className="ai-steps-label">Steps</div>
              <div className="ai-steps">
                <table>
                  <thead>
                    <tr><th className="ai-steps-num">#</th><th>Step description</th><th>Expected results</th><th /></tr>
                  </thead>
                  <tbody>
                    {d.steps.map((s, si) => (
                      <tr key={si}>
                        <td className="ai-steps-num">{si + 1}</td>
                        <td>
                          <textarea
                            value={s.description}
                            onChange={(e) => patchStep(d.key, si, 'description', e.target.value)}
                            disabled={d._status === 'added' || d._status === 'adding'}
                            rows={2}
                          />
                        </td>
                        <td>
                          <textarea
                            value={s.expected}
                            onChange={(e) => patchStep(d.key, si, 'expected', e.target.value)}
                            disabled={d._status === 'added' || d._status === 'adding'}
                            rows={2}
                          />
                        </td>
                        <td className="ai-steps-del">
                          {d._status !== 'added' && d._status !== 'adding' && (
                            <button title="Remove step" onClick={() => removeStepRow(d.key, si)}>×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {d._status !== 'added' && d._status !== 'adding' && (
                  <button className="ai-steps-add" onClick={() => addStepRow(d.key)}>＋ Add step</button>
                )}
              </div>
              </>
            ) : (
              <HtmlEditable
                className="ai-card-desc ai-card-desc-html"
                value={d.description}
                onChange={(html) => patch(d.key, { description: html })}
                placeholder="Description"
                disabled={d._status === 'added' || d._status === 'adding'}
              />
            )}

            {d._error && <div className="ai-error ai-card-err">{d._error}</div>}
          </div>
          );
        })}
      </div>

      <div className="ai-foot">
        <span className="ai-foot-note">Edit anything before adding. Each item is created &amp; traced to {ctx.parentKey}{ctx.linkType ? ` as “${ctx.linkType}”` : ''}.</span>
        <button className="btn-primary" onClick={onClose}>Done</button>
      </div>
    </>
  );
}

/** The matrix panel's routes — the default `api` (see AddItemModal). */
function matrixApi(setupId, ctx) {
  const post = async (path, body) => {
    const resp = await fetch(`/api/matrix/${setupId}/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    return { ok: resp.ok, data };
  };
  return {
    key: `${setupId}|${ctx.parentLevelNum}|${ctx.levelNum}|${ctx.childBlockId || ''}`,
    placement: true,
    checkAi: () => {
      const qs = new URLSearchParams({
        source_level_num: String(ctx.parentLevelNum),
        child_level_num: String(ctx.levelNum),
      });
      if (ctx.childBlockId) qs.set('child_block_id', ctx.childBlockId);
      return fetch(`/api/matrix/${setupId}/ai/prompt-available?${qs}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null));
    },
    generate: () => post('ai/generate', {
      source_key: ctx.parentKey,
      source_level_num: ctx.parentLevelNum,
      child_level_num: ctx.levelNum,
      child_block_id: ctx.childBlockId || null,
    }),
    add: ({ name, description, steps, placeAt }) => post('add-item', {
      level_num: ctx.levelNum,
      parent_key: ctx.parentKey,
      name,
      description,
      child_key_prefix: ctx.childKeyPrefix || null,
      // A depth can hold several sibling block types (e.g. Complaint → CAPA / Non
      // Conformance / Risk). Without this, the backend falls back to the FIRST block
      // at the depth and creates the wrong type (the reported RISK-instead-of-NCR bug).
      child_block_id: ctx.childBlockId || null,
      steps,
      // DHF: under this paragraph of this document; null = the pool, as before.
      doc_parent_id: placeAt?.parentId || null,
      doc_id: placeAt?.docId || null,
      doc_parent_label: placeAt?.parentLabel || null,
    }),
    dupCheck: (drafts) => post('ai/dup-check', {
      parent_key: ctx.parentKey,
      child_level_num: ctx.levelNum,
      child_block_id: ctx.childBlockId || null,
      drafts,
    }),
  };
}

export default AddItemModal;
